import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from '@/hooks/useAuth';
import { ThemeProvider } from '@/hooks/useTheme';
import { ProjectProvider } from '@/hooks/useProject';
import Layout from '@/components/Layout/Layout';
import ProtectedRoute from '@/components/Auth/ProtectedRoute';
import RequireFeatureFlag from '@/components/Auth/RequireFeatureFlag';
import RequireOrgFeature from '@/components/Auth/RequireOrgFeature';
import AcceptInvitationPage from '@/pages/Auth/AcceptInvitationPage';
// Pages
import HomePage from '@/pages/Home/HomePage';
import LoginPage from '@/pages/Auth/LoginPage';
import RegisterPage from '@/pages/Auth/RegisterPage';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import AnalyticsDashboardPage from '@/pages/Analytics/AnalyticsDashboardPage';
import ChatPage from '@/pages/Chat/ChatPage';
import ProfilePage from '@/pages/Profile/ProfilePage';
import SettingsPage from '@/pages/Settings/SettingsPage';
import IntegrationsPage from '@/pages/Settings/IntegrationsPage';
import OrganizationPage from '@/pages/Organization/OrganizationPage';
import ProjectsPage from '@/pages/Projects/ProjectsPage';
import ProjectFilesPage from '@/pages/Projects/ProjectFilesPage';
import NotFoundPage from '@/pages/NotFound/NotFoundPage';
import UsersPage from '@/pages/Users/UsersPage';
import VideosPage from '@/pages/Video/VideosPage';
import RolesPage from '@/pages/Roles/RolesPage';
import TeamsPage from '@/pages/Teams/TeamsPage';
import MyAssignmentsPage from '@/pages/Assignments/MyAssignmentsPage';
// Audio Pages
import VoiceManagement from '@/pages/Audio/VoiceManagement';
import AudioTranslation from '@/pages/Audio/AudioTranslation';
import TextTranslationPage from '@/pages/TextTranslation/TextTranslationPage';
import SpeechToText from '@/pages/Audio/SpeechToText';
import TextToSpeech from '@/pages/Audio/TextToSpeech';
import TranslationJobs from '@/pages/Audio/TranslationJobs';
import VideoTranslation from '@/pages/Audio/VideoTranslation';
import AvatarLibrary from '@/pages/Audio/AvatarLibrary';
import OnboardingPage from '@/pages/Onboarding/OnboardingPage';
import SelectOrganizationPage from '@/pages/Auth/SelectOrganizationPage';
import CheckEmailPage from '@/pages/Auth/CheckEmailPage';
import VerifyEmailPage from '@/pages/Auth/VerifyEmailPage';
import SuperAdminPage from '@/pages/SuperAdmin/SuperAdminPage';
import PlanManagementPage from '@/pages/SuperAdmin/PlanManagementPage';
import ClipGeneratorPage from '@/pages/Clips/ClipGeneratorPage';
import CreateVideoPage from '@/pages/CreateVideo/CreateVideoPage';
import ScheduledPostsPage from '@/pages/Publishing/ScheduledPostsPage';
import ThumbnailGeneratorPage from '@/pages/Clips/ThumbnailGeneratorPage';
import VideoUnderstandingPage from '@/pages/VideoUnderstanding/VideoUnderstandingPage';
import VideoEditorPage from '@/pages/VideoEditor/VideoEditorPage';
import ConfigurationPage from '@/pages/Configuration/ConfigurationPage';
import PricingPage from '@/pages/Pricing/PricingPage';
import BillingPage from '@/pages/Settings/BillingPage';
import NewsroomDashboard from '@/pages/Newsroom/NewsroomDashboard';
import NewsSourcesPage from '@/pages/Newsroom/NewsSourcesPage';
import ArticleReviewPage from '@/pages/Newsroom/ArticleReviewPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ProjectProvider>
            <Router>
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/check-email" element={<CheckEmailPage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
                
                {/* Protected Routes */}
                <Route path="/app" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<DashboardPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="my-assignments" element={<MyAssignmentsPage />} />
                  <Route path="analytics" element={<AnalyticsDashboardPage />} />
                  <Route path="organization" element={<OrganizationPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="projects/files/:type" element={<ProjectFilesPage />} />
                  <Route path="chat" element={<ChatPage />} />
                  <Route path="chat/:conversationId" element={<ChatPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="settings/billing" element={<BillingPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="configurations" element={<ConfigurationPage />} />
                  <Route path="videos" element={<VideosPage />} />
                  <Route path="clips" element={<ClipGeneratorPage />} />
                  <Route path="create-video" element={<CreateVideoPage />} />
                  <Route path="scheduled-posts" element={<ScheduledPostsPage />} />
                  <Route path="video-understanding" element={
                    <RequireFeatureFlag featureKey="video-understanding">
                      <VideoUnderstandingPage />
                    </RequireFeatureFlag>
                  } />
                  <Route path="thumbnails" element={<ThumbnailGeneratorPage />} />
                  <Route path="editor" element={
                    <RequireFeatureFlag featureKey="video-editor">
                      <VideoEditorPage />
                    </RequireFeatureFlag>
                  } />
                  <Route path="editor/:projectId" element={
                    <RequireFeatureFlag featureKey="video-editor">
                      <VideoEditorPage />
                    </RequireFeatureFlag>
                  } />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="roles" element={<RolesPage />} />
                  <Route path="teams" element={<TeamsPage />} />
                  <Route path="superadmin" element={<SuperAdminPage />} />
                  <Route path="superadmin/plans" element={<PlanManagementPage />} />

                  {/* Newsroom add-on — per-org opt-in, gated by RequireOrgFeature */}
                  <Route path="newsroom" element={
                    <RequireOrgFeature feature="newsroom">
                      <NewsroomDashboard />
                    </RequireOrgFeature>
                  } />
                  <Route path="newsroom/sources" element={
                    <RequireOrgFeature feature="newsroom">
                      <NewsSourcesPage />
                    </RequireOrgFeature>
                  } />
                  <Route path="newsroom/articles/:id" element={
                    <RequireOrgFeature feature="newsroom">
                      <ArticleReviewPage />
                    </RequireOrgFeature>
                  } />
                  
                  {/* Audio Feature Routes */}
                  <Route path="audio">
                    <Route path="voices" element={<VoiceManagement />} />
                    <Route path="translation" element={<AudioTranslation />} />
                    <Route path="text-translation" element={<TextTranslationPage />} />
                    <Route path="speech-to-text" element={<SpeechToText />} />
                    <Route path="text-to-speech" element={<TextToSpeech />} />
                    <Route path="jobs" element={<TranslationJobs />} />
                    <Route path="video-translation" element={<VideoTranslation />} />
                    <Route path="avatars" element={<AvatarLibrary />} />
                  </Route>
                </Route>
                
                {/* Onboarding Route (auth required, no Layout/sidebar) */}
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } />

                {/* Organization Selection Route (auth required, no Layout/sidebar) */}
                <Route path="/select-organization" element={
                  <ProtectedRoute>
                    <SelectOrganizationPage />
                  </ProtectedRoute>
                } />

                {/* 404 Route */}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
                
                {/* Global Toast Notifications */}
                <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#22c55e',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </ProjectProvider>
        </AuthProvider>
      </ThemeProvider>
      
      {/* React Query DevTools - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
